import axios from 'axios';
import { API_URL } from '../../config';

const api = axios.create({
  baseURL: API_URL,
  withCredentials: true,
});

function normalizeNumericId(value) {
  if (value == null || value === '') {
    return value;
  }

  const parsed = Number.parseInt(String(value), 10);
  return Number.isFinite(parsed) ? parsed : value;
}

export async function fetchUserBoards() {
  const { data } = await api.get('/user/data');
  return data?.boards || [];
}

export async function fetchInvestorById(investorId) {
  try {
    const identifier = String(investorId).trim();
    const { data } = await api.get(`/investors/${identifier}`, {
      params: { by: '_id' },
    });
    return data;
  } catch {
    return {
      _id: investorId,
      id: investorId,
      name: 'Unknown',
      country: '',
      type: [],
    };
  }
}

export async function fetchInvestorStatsById(investorId) {
  try {
    const identifier = String(investorId).trim();
    const { data } = await api.get(`/investors/${identifier}/stats`, {
      params: { by: '_id' },
    });
    return data || {
      deals_total: 0,
      lead_only: 0,
      second_only: 0,
      both: 0,
      years: [],
    };
  } catch {
    return {
      deals_total: 0,
      lead_only: 0,
      second_only: 0,
      both: 0,
      years: [],
    };
  }
}

export async function fetchInvestorsByName(keyword) {
  const { data } = await api.get('/investors', {
    params: {
      name: keyword,
      limit: 10,
    },
  });

  return Array.isArray(data?.investors) ? data.investors : [];
}

export async function fetchInvestorsPage() {
  const { data } = await api.get('/investors', {
    params: {
      limit: 25,
      summary: 1,
      preferDealsFirst: true,
    },
  });
  return Array.isArray(data?.investors) ? data.investors : [];
}

export function createBoard(boardName) {
  return api.post('/user/data/board/create', { board_name: boardName });
}

export function updateBoardName(boardId, newName) {
  return api.post('/user/data/board/update-name', {
    board_id: normalizeNumericId(boardId),
    new_name: newName,
  });
}

export function deleteBoard(boardId) {
  return api.post('/user/data/board/delete', {
    board_id: normalizeNumericId(boardId),
  });
}

export function updateTargetAmountForBoard(boardId, targetAmount) {
  return api.post('/user/data/board/update-target', {
    board_id: normalizeNumericId(boardId),
    target_amount: targetAmount,
  });
}

export function addInvestorToBoard(boardId, columnId, investorId) {
  return api.post('/user/data/board/add-investor', {
    board_id: normalizeNumericId(boardId),
    column_id: normalizeNumericId(columnId),
    investor_id: investorId,
    note: '',
    value: 0,
  });
}

export function removeInvestorFromBoard(boardId, columnId, investorId) {
  return api.post('/user/data/board/remove-investor', {
    board_id: normalizeNumericId(boardId),
    column_id: normalizeNumericId(columnId),
    investor_id: investorId,
  });
}

export function updateInvestorPosition(boardId, sourceColumnId, destinationColumnId, investorId, newPosition) {
  return api.post('/user/data/board/update-investor-position', {
    board_id: normalizeNumericId(boardId),
    sourceColumnId: normalizeNumericId(sourceColumnId),
    destinationColumnId: normalizeNumericId(destinationColumnId),
    investor_id: investorId,
    newPosition: normalizeNumericId(newPosition),
  });
}

export function updateInvestorNote(boardId, columnId, investorId, note) {
  return api.post('/user/data/board/add-note-investor', {
    board_id: normalizeNumericId(boardId),
    column_id: normalizeNumericId(columnId),
    investor_id: investorId,
    note: note || '',
  });
}

export function updateInvestorValue(boardId, columnId, investorId, value) {
  return api.post('/user/data/board/add-value-investor', {
    board_id: normalizeNumericId(boardId),
    column_id: normalizeNumericId(columnId),
    investor_id: investorId,
    value: value || 0,
  });
}

export function updateColumnOrder(boardId, columnId, newPosition) {
  return api.post('/user/data/board/update-column-order', {
    board_id: normalizeNumericId(boardId),
    column_id: normalizeNumericId(columnId),
    position: normalizeNumericId(newPosition),
  });
}

export function renameColumn(boardId, columnId, newName) {
  return api.post('/user/data/board/update-column-name', {
    board_id: normalizeNumericId(boardId),
    column_id: normalizeNumericId(columnId),
    new_name: newName,
  });
}

export function deleteColumn(boardId, columnId) {
  return api.post('/user/data/board/delete-column', {
    board_id: normalizeNumericId(boardId),
    column_id: normalizeNumericId(columnId),
  });
}

export function updateBoardColumns(boardId, columns) {
  return api.post('/user/data/board/update-columns', {
    board_id: normalizeNumericId(boardId),
    columns: (columns || []).map((column, index) => ({
      column_id: normalizeNumericId(column?.column_id),
      column_name: String(column?.column_name || '').trim(),
      position: normalizeNumericId(column?.position ?? index + 1),
    })),
  });
}

export function addNewColumn(boardId, columnName) {
  return api.post('/user/data/board/add-column', {
    board_id: normalizeNumericId(boardId),
    column_name: columnName,
  });
}
